import { Loader2 } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { prefixLibraryLabel, type LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import { LibraryNodeEmpty, LibraryNodeError, LibraryNodeLoading } from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import {
  CANVAS_INITIAL_PAGE_SIZE,
  CANVAS_PAGE_SIZE,
  type CanvasLibraryReference,
  fetchCanvasBoardReferenceLibrary,
  fetchCanvasGenerations,
} from "@/lib/canvas-api"
import { canvasGenerationSource, libraryKindFromMediaType, projectUploadSource } from "@/lib/library-source"
import type { LibraryViewMode } from "@/lib/library-view-mode"
import type { CanvasGenerationRow } from "@/types/canvas"

type LibraryCanvasNodeProps = {
  projectId: number
  canvasId: number
  mode: LibraryViewMode
  /** Delivery FPS проекта для сетки таймлайна в просмотрщике. */
  frameRate?: number
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
}

const NO_ITEMS: LibraryItem[] = []

function generationPosterUrl(generation: CanvasGenerationRow): string | null {
  return (
    generation.media_thumb_url ??
    generation.first_frame_thumb_url ??
    (generation.media_kind === "image" ? generation.media_url : null)
  )
}

/**
 * Папка «доска Холста»: завершённые генерации с курсорной пагинацией
 * (сентинел IntersectionObserver — паттерн Canvas.tsx); просмотр — в общем
 * просмотрщике Библиотеки, как у остальных ассетов.
 */
export function LibraryCanvasNode({
  projectId,
  canvasId,
  mode,
  frameRate,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
}: LibraryCanvasNodeProps) {
  const { t } = useTranslation(["workspace", "common"])
  const [items, setItems] = useState<CanvasGenerationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [uploads, setUploads] = useState<CanvasLibraryReference[]>([])
  const [uploadsLoading, setUploadsLoading] = useState(true)
  const [uploadsFailed, setUploadsFailed] = useState(false)
  const pageRef = useRef({ cursor: null as number | null, hasMore: false, loadingMore: false })
  // Сентинел держим состоянием, а не ref: группа сворачивается и размонтирует его,
  // а object-ref не перезапускает эффект — наблюдатель оставался бы на отсоединённом узле
  // и догрузка умирала бы до ухода из папки.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    setItems([])
    setLoading(true)
    setLoadFailed(false)
    setHasMore(false)
    pageRef.current = { cursor: null, hasMore: false, loadingMore: false }
    fetchCanvasGenerations(projectId, canvasId, { limit: CANVAS_INITIAL_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return
        setItems(page.generations)
        setHasMore(page.has_more)
        pageRef.current = { cursor: page.next_cursor, hasMore: page.has_more, loadingMore: false }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, canvasId, reloadKey])

  // Загрузки доски грузим отдельно от генераций: список короткий и без пагинации,
  // а неудача здесь не должна прятать генерации — поэтому своя ошибка не заводится.
  useEffect(() => {
    const controller = new AbortController()
    setUploads([])
    setUploadsLoading(true)
    setUploadsFailed(false)
    fetchCanvasBoardReferenceLibrary(projectId, canvasId, { signal: controller.signal })
      .then(setUploads)
      .catch(() => {
        if (!controller.signal.aborted) setUploadsFailed(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setUploadsLoading(false)
      })
    return () => controller.abort()
  }, [projectId, canvasId, reloadKey])

  const loadMore = useCallback(async () => {
    const { cursor, hasMore: canLoad, loadingMore: inFlight } = pageRef.current
    if (inFlight || !canLoad || cursor == null) return
    pageRef.current = { ...pageRef.current, loadingMore: true }
    setLoadingMore(true)
    try {
      const page = await fetchCanvasGenerations(projectId, canvasId, { before: cursor, limit: CANVAS_PAGE_SIZE })
      setItems((prev) => {
        const known = new Set(prev.map((g) => g.id))
        return [...prev, ...page.generations.filter((g) => !known.has(g.id))]
      })
      setHasMore(page.has_more)
      pageRef.current = { cursor: page.next_cursor, hasMore: page.has_more, loadingMore: false }
    } catch {
      pageRef.current = { ...pageRef.current, loadingMore: false }
    } finally {
      setLoadingMore(false)
    }
  }, [projectId, canvasId])

  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  useEffect(() => {
    if (!sentinel || !hasMore || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMoreRef.current()
        }
      },
      { rootMargin: "400px 0px" },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
    // items.length в зависимостях обязателен: колбэк наблюдателя срабатывает только на
    // СМЕНУ пересечения, а страница могла не принести ни одной видимой плитки (все
    // генерации не completed) — сентинел остался бы в зоне видимости без нового
    // события, и догрузка встала бы на пустой папке.
  }, [sentinel, hasMore, loading, items.length])

  if (loading || loadFailed) {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {loading ? <LibraryNodeLoading /> : <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} />}
      </>
    )
  }

  const completed = items.filter((g) => g.status === "completed")
  // «Пусто» показываем, только когда загрузки уже ответили: иначе отказ или гонка
  // выглядели бы как пустая доска.
  if (completed.length === 0 && !hasMore && uploads.length === 0 && !uploadsLoading && !uploadsFailed) {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        <LibraryNodeEmpty>{t("library.canvas.empty")}</LibraryNodeEmpty>
      </>
    )
  }

  const uploadsLabel = t("library.canvas.uploadsGroup")
  const uploadItems: LibraryItem[] = uploads.map((reference) => {
    const kind = libraryKindFromMediaType(reference.media_type)
    const isVideo = kind === "video"
    const durationSeconds = reference.duration_seconds
    const label = prefixLibraryLabel(uploadsLabel, reference.label)
    return {
      key: `upload-${reference.signed_id}`,
      kind,
      label,
      createdAt: reference.created_at,
      // thumb_url только у картинок; у video/audio не подставлять файл в <img>.
      thumbUrl: reference.thumb_url ?? (kind === "image" ? reference.url : null),
      source: reference.id != null ? projectUploadSource(reference.id) : undefined,
      asset: {
        title: label,
        kind,
        // Просмотрщик: image+audio из url, video из videoUrl (same-origin stream).
        url: isVideo ? null : reference.url,
        videoUrl: isVideo ? reference.url : null,
        downloadUrl: reference.download_url ?? reference.url,
        createdAt: reference.created_at,
        ...(typeof durationSeconds === "number" && Number.isFinite(durationSeconds) && durationSeconds > 0
          ? { durationMs: durationSeconds * 1000 }
          : {}),
      },
    }
  })

  const libraryItems: LibraryItem[] = completed.map((generation) => {
    const isVideo = generation.media_kind === "video"
    const label = generation.display_prompt.trim() || t("library.canvas.generationLabel", { id: generation.number })
    return {
      key: String(generation.id),
      kind: isVideo ? "video" : "image",
      label,
      createdAt: generation.created_at,
      thumbUrl: generationPosterUrl(generation),
      source: canvasGenerationSource(generation.id),
      asset: {
        title: label,
        kind: isVideo ? "video" : "image",
        url: isVideo ? null : generation.media_url,
        videoUrl: isVideo ? generation.media_url : null,
        downloadUrl: generation.download_url ?? generation.media_url,
        createdAt: generation.created_at,
        durationMs: generation.duration_ms ?? null,
        frameRate: frameRate ?? null,
        sourceId: generation.id,
      },
    }
  })

  // Один список: сортировка по дате может поставить загрузку выше старой генерации.
  // Исходный порядок (генерации, потом загрузки) остаётся тай-брейком при равной дате.
  const allItems = uploadItems.length > 0 ? [...libraryItems, ...uploadItems] : libraryItems

  return (
    <>
      <LibraryItemsReporter items={allItems} onItemsChange={onItemsChange} />
      {allItems.length > 0 ? (
        <LibraryItems
          items={allItems}
          mode={mode}
          aspectClass="aspect-square"
          listLayout="table"
          focusedKey={focusedKey}
          onFocusItem={onFocusItem}
          onOpenItem={onOpenItem}
        />
      ) : null}
      {hasMore ? (
        <div ref={setSentinel} className="flex justify-center py-4">
          {loadingMore ? <Loader2 className="h-4 w-4 animate-spin text-ui-text-muted" /> : null}
        </div>
      ) : null}
      {/* Отказ загрузок нельзя проглатывать молча: в папке, где ещё нет готовых
          генераций, это давало пустое место без единого слова и без повтора. */}
      {uploadsFailed ? <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} /> : null}
    </>
  )
}
